import { FC, useEffect } from 'react';
import TableModule from './TableModule';
import {
  OperatingIncomeNames,
  CostExpenseConfig,
  EmployeeCompensationCostNames,
  StoreImageCostNames,
  StoreEngineeringCostNames,
  LeaseCostNames,
  OtherCostNames,
  CostExpenseNames } from '../ts-config';
import { Form } from 'antd';
import { isNotEmpty } from '@lhb/func';
import {
  fieldNameOperatingIncome,
  fieldNameCostExpense,
  fieldNameEmployeeCost,
  fieldNameStoreImageCost,
  fieldNameStoreEngineeringCost,
  fieldNameLeaseCost,
  fieldNameOtherCost
} from './fieldname';
import { fixIntParam, fixPrecisionParam } from './transfer2value';

/**
 * @Description 费用成本
 */
const CostExpense: FC<{
  form: any;
  formItemData: any;
  disabled?: boolean;
  onChange: any;
}> = ({
  form,
  formItemData,
  disabled,
  onChange
}) => {
  // 职工薪酬（第一年）
  const employeeSalaryFirst: any = Form.useWatch(
    fieldNameEmployeeCost(true, EmployeeCompensationCostNames.employeeSalary),
    form
  );
  // 职工薪酬（第二年）
  const employeeSalarySecond: any = Form.useWatch(
    fieldNameEmployeeCost(false, EmployeeCompensationCostNames.employeeSalary),
    form
  );

  // 店铺形象费用
  const storeImageCost: any = Form.useWatch(
    fieldNameStoreImageCost(StoreImageCostNames.storeImageCost),
    form
  );
  // 店铺工程成本
  const storeEngineeringCost: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.storeEngineeringCost),
    form
  );

  // 租赁成本（第一年）
  const leaseCostFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.leaseCost),
    form
  );
  // 租赁成本（第二年）
  const leaseCostSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.leaseCost),
    form
  );

  // 其他成本（第一年）
  const otherCostFirst: any = Form.useWatch(
    fieldNameOtherCost(true, OtherCostNames.otherCost),
    form
  );
  // 其他成本（第二年）
  const otherCostSecond: any = Form.useWatch(
    fieldNameOtherCost(false, OtherCostNames.otherCost),
    form
  );

  // 财务毛利额（第一年）
  const financialGrossProfitFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.financialGrossProfit),
    form
  );
  // 财务毛利额（第二年）
  const financialGrossProfitSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.financialGrossProfit),
    form
  );

  // 费用总额（第一年）
  const totalFeesFirst: any = Form.useWatch(
    fieldNameCostExpense(true, CostExpenseNames.totalFees),
    form
  );
  // 费用总额（第二年）
  const totalFeesSecond: any = Form.useWatch(
    fieldNameCostExpense(false, CostExpenseNames.totalFees),
    form
  );

  // 利润总额（第一年）
  const totalProfitFirst: any = Form.useWatch(
    fieldNameCostExpense(true, CostExpenseNames.totalProfit),
    form
  );
  // 利润总额（第二年）
  const totalProfitSecond: any = Form.useWatch(
    fieldNameCostExpense(false, CostExpenseNames.totalProfit),
    form
  );

  // 利润率（第一年）
  const profitMarginFirst: any = Form.useWatch(
    fieldNameCostExpense(true, CostExpenseNames.profitMargin),
    form
  );
    // 利润率（第二年）
  const profitMarginSecond: any = Form.useWatch(
    fieldNameCostExpense(false, CostExpenseNames.profitMargin),
    form
  );

  // 营业收入（第一年）
  const operatingIncomeFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.operatingIncome),
    form
  );
  // 营业收入（第二年）
  const operatingIncomeSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.operatingIncome),
    form
  );

  // 费用总额
  useEffect(() => {
    const name = fieldNameCostExpense(true, CostExpenseNames.totalFees);
    const val: any = calcTotalFees(employeeSalaryFirst, storeImageCost, storeEngineeringCost, leaseCostFirst, otherCostFirst);
    form.setFieldValue(name, val);
  }, [employeeSalaryFirst, storeImageCost, storeEngineeringCost, leaseCostFirst, otherCostFirst]);

  useEffect(() => {
    const name = fieldNameCostExpense(false, CostExpenseNames.totalFees);
    const val: any = calcTotalFees(employeeSalarySecond, storeImageCost, storeEngineeringCost, leaseCostSecond, otherCostSecond);
    form.setFieldValue(name, val);
  }, [employeeSalarySecond, storeImageCost, storeEngineeringCost, leaseCostSecond, otherCostSecond]);

  /**
   * @description 费用总额
   * @param employeeSalary
   * @param storeImageCost
   * @param storeEngineeringCost
   * @param leaseCost
   * @param otherCost
   * @return 职工薪酬+店铺形象费用+店铺工程费用+租赁成本+其他成本
   */
  const calcTotalFees = (employeeSalary, storeImageCost, storeEngineeringCost, leaseCost, otherCost) => {
    let val: any = null;

    isNotEmpty(employeeSalary) && (val += (+employeeSalary));
    isNotEmpty(storeImageCost) && (val += (+storeImageCost));
    isNotEmpty(storeEngineeringCost) && (val += (+storeEngineeringCost));
    isNotEmpty(leaseCost) && (val += (+leaseCost));
    isNotEmpty(otherCost) && (val += (+otherCost));

    isNotEmpty(val) && (val = fixIntParam(val));
    return val;
  };

  // 利润总额
  useEffect(() => {
    const name = fieldNameCostExpense(true, CostExpenseNames.totalProfit);
    const val: any = calcTotalProfit(financialGrossProfitFirst, totalFeesFirst);
    form.setFieldValue(name, val);
  }, [financialGrossProfitFirst, totalFeesFirst]);

  useEffect(() => {
    const name = fieldNameCostExpense(false, CostExpenseNames.totalProfit);
    const val: any = calcTotalProfit(financialGrossProfitSecond, totalFeesSecond);
    form.setFieldValue(name, val);
  }, [financialGrossProfitSecond, totalFeesSecond]);

  /**
   * @description 利润总额
   * @param financialGrossProfit 财务毛利额
   * @param totalFees 费用总额
   * @return 财务毛利额-费用总额
   */
  const calcTotalProfit = (financialGrossProfit, totalFees) => {
    return isNotEmpty(financialGrossProfit) && isNotEmpty(totalFees) ? fixIntParam((+financialGrossProfit) - (+totalFees)) : null;
  };

  // 利润率
  useEffect(() => {
    const name = fieldNameCostExpense(true, CostExpenseNames.profitMargin);
    const val: any = calcProfitMargin(totalProfitFirst, operatingIncomeFirst);
    form.setFieldValue(name, val);
  }, [totalProfitFirst, operatingIncomeFirst]);

  useEffect(() => {
    const name = fieldNameCostExpense(false, CostExpenseNames.profitMargin);
    const val: any = calcProfitMargin(totalProfitSecond, operatingIncomeSecond);
    form.setFieldValue(name, val);
  }, [totalProfitSecond, operatingIncomeSecond]);

  /**
   * @description 利润率
   * @param totalProfit 利润总额
   * @param operatingIncome 营业收入
   * @return 利润总额/营业收入
   */
  const calcProfitMargin = (totalProfit, operatingIncome) => {
    return isNotEmpty(totalProfit) && operatingIncome ? fixPrecisionParam((+totalProfit) * 100.0 / (+operatingIncome)) : null;
  };

  /**
   * @description 特殊处理，自动计算结果变动时，需要触发计算
   */
  useEffect(() => {
    onChange && onChange();
  }, [totalFeesFirst, totalFeesSecond, totalProfitFirst, totalProfitSecond, profitMarginFirst, profitMarginSecond]);

  return (
    <TableModule
      moduleConfig={CostExpenseConfig}
      form={form}
      formItemData={formItemData}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export default CostExpense;
